/*
 * WikiHelp — in-app help that shows a wiki page (Erwin, 2026-10-27: one source of truth —
 * information for users lives in the wiki, User guide section, and the console's ⓘ shows the
 * page). Pages are markdown in this repo's docs/guide/, synced into the aquastar wiki space
 * (see _wiki.yml); slug = guide/admin-console/<file name>.
 *
 * The body is the gateway's rendered HTML (markdown-it with html:false — raw HTML in a page is
 * escaped, javascript:/data: hrefs dropped), the same HTML the Wiki page itself renders.
 *
 * Inside the body:
 *   - `[prompt](#ask)` links become buttons that call onAsk(text) (example prompts);
 *   - links to other wiki pages open that page in the console's Wiki;
 *   - #heading links scroll within the popup; external links open in a new tab.
 */

import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { appRest } from '../lib/apiClient';

export interface WikiHelpPage {
  slug: string;
  title: string;
  bodyHtml: string;
}

const cache = new Map<string, WikiHelpPage>();

/** Test hook: forget cached pages. */
export function clearWikiHelpCache(): void {
  cache.clear();
}

export async function loadWikiPage(slug: string): Promise<WikiHelpPage> {
  const hit = cache.get(slug);
  if (hit) return hit;
  const r = await appRest<{ page: WikiHelpPage }>('GET', `/wiki/pages/${slug}`);
  const page = { slug: r.page.slug, title: r.page.title, bodyHtml: r.page.bodyHtml };
  cache.set(slug, page);
  return page;
}

export const WIKI_HELP_CSS = `
.aqua-wiki-help { line-height: 1.55; font-size: .9rem; overflow-wrap: anywhere; }
.aqua-wiki-help h1 { display: none; }
.aqua-wiki-help h2 { font-size: 1.05rem; margin: 1.1rem 0 .4rem; padding-bottom: .2rem; border-bottom: 1px solid var(--bs-border-color, #dee2e6); }
.aqua-wiki-help h3 { font-size: .9rem; font-weight: 600; margin: .8rem 0 .25rem; }
.aqua-wiki-help p, .aqua-wiki-help ul, .aqua-wiki-help ol { margin-bottom: .5rem; }
.aqua-wiki-help ul, .aqua-wiki-help ol { padding-left: 1.1rem; }
.aqua-wiki-help table { border-collapse: collapse; width: 100%; margin: .5rem 0; font-size: .85rem; }
.aqua-wiki-help th, .aqua-wiki-help td { border: 1px solid var(--bs-border-color, #dee2e6); padding: .25rem .45rem; vertical-align: top; }
.aqua-wiki-help :not(pre) > code { background: rgba(0,0,0,.06); padding: .05em .3em; border-radius: 4px; font-size: .9em; }
.aqua-wiki-help pre { background: rgba(0,0,0,.05); padding: .5rem .75rem; border-radius: 6px; overflow: auto; }
.aqua-wiki-help img { max-width: 100%; }
.aqua-wiki-help li:has(> a[href="#ask"]) { list-style: none; margin-left: -1.1rem; }
.aqua-wiki-help a[href="#ask"] { display: inline-block; padding: .2rem .5rem; margin: .1rem 0; border: 1px solid var(--bs-primary, #0d6efd); border-radius: .375rem; text-decoration: none; }
.aqua-wiki-help a[href="#ask"]:hover { background: var(--bs-primary, #0d6efd); color: #fff; }
.aqua-wiki-help.no-ask a[href="#ask"] { border: 0; padding: 0; pointer-events: none; color: inherit; }
`;

type State = { kind: 'loading' } | { kind: 'ok'; page: WikiHelpPage } | { kind: 'error'; message: string };

/** The rendered page body (no chrome). */
export function WikiHelpBody({ slug, onAsk }: { slug: string; onAsk?: (text: string) => void }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useGo();

  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    loadWikiPage(slug).then(
      (page) => live && setState({ kind: 'ok', page }),
      (e: unknown) => {
        if (!live) return;
        const status = (e as { status?: number })?.status;
        setState({
          kind: 'error',
          message:
            status === 404
              ? 'This help page is not in the wiki yet.'
              : status === 403
                ? 'You do not have access to the wiki.'
                : 'Could not load the help page.',
        });
      },
    );
    return () => {
      live = false;
    };
  }, [slug]);

  // External links open in a new tab.
  useEffect(() => {
    if (state.kind !== 'ok' || !ref.current) return;
    ref.current.querySelectorAll<HTMLAnchorElement>('a[href^="http"]').forEach((a) => {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    });
  }, [state]);

  function onClick(e: MouseEvent<HTMLDivElement>) {
    const a = (e.target as HTMLElement).closest('a');
    if (!a || !ref.current?.contains(a)) return;
    const href = a.getAttribute('href') ?? '';
    if (href === '#ask') {
      e.preventDefault();
      const text = (a.textContent ?? '').trim();
      if (onAsk && text) onAsk(text);
      return;
    }
    const wikiSlug = a.getAttribute('data-wiki-page');
    if (wikiSlug) {
      e.preventDefault();
      navigate(`/wiki/${wikiSlug}${href.includes('#') ? href.slice(href.indexOf('#')) : ''}`);
      return;
    }
    if (href.startsWith('#') && href.length > 1) {
      e.preventDefault();
      let id = href.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        /* malformed %-escape: use it as written */
      }
      ref.current.querySelector(`[id="${CSS.escape(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (state.kind === 'loading') {
    return (
      <div className="text-muted small py-3" data-testid="wiki-help-loading">
        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
        Loading help…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="alert alert-light border small my-2" role="alert" data-testid="wiki-help-error">
        {state.message}{' '}
        <a href={`/wiki/${slug}`} onClick={(e) => { e.preventDefault(); navigate(`/wiki/${slug}`); }}>
          Open the wiki
        </a>
      </div>
    );
  }
  return (
    <>
      <style>{WIKI_HELP_CSS}</style>
      <div
        ref={ref}
        className={`aqua-wiki-help${onAsk ? '' : ' no-ask'}`}
        data-testid="wiki-help-body"
        onClick={onClick}
        // Gateway-rendered wiki HTML (markdown-it, html:false) — the same HTML the Wiki page renders.
        dangerouslySetInnerHTML={{ __html: state.page.bodyHtml }}
      />
    </>
  );
}

/**
 * In-app navigation inside the router; a plain page load outside it (the Settings dialog can be
 * mounted on its own). Whether there is a router never changes for a mounted component, so the
 * conditional hook call below is stable.
 */
function useGo(): (to: string) => void {
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = inRouter ? useNavigate() : null;
  return navigate ?? ((to: string) => window.location.assign(to));
}

/** "Open in wiki ↗" link for a help page. */
export function OpenInWiki({ slug, children = 'Open in wiki' }: { slug: string; children?: ReactNode }) {
  return (
    <a href={`/wiki/${slug}`} target="_blank" rel="noopener noreferrer" className="small" data-testid="wiki-help-open">
      {children} ↗
    </a>
  );
}

/** Wiki slugs of the console's help pages (docs/guide/<file>.md). */
export const HELP_PAGES = {
  assistant: 'guide/admin-console/admin-assistant',
  googleSignIn: 'guide/admin-console/google-sign-in',
} as const;
